import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { type CreateCategoryPayload, createCategory } from '@/api/category';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import { notifyUser } from '@/store/Toast';
import type { Category } from '@/types/Category';
import { sanitizeResponseError } from '@/utils';

interface NewCategoryFormProps {
  handleModal: () => void;
}

const NewSubCategoryBaseForm = (props: NewCategoryFormProps) => {
  const { handleModal } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [parentCategory, _setParentCategory] = useState<Category | null>(null);

  const postNewCategory = useMutation((data: { payload: CreateCategoryPayload; type: string }) => createCategory(data.payload), {
    onSuccess: (_, { type }) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Category added successfully',
          toastType: 'success',
        })
      );
      handleModal();
      queryClient.invalidateQueries([ReactQueryKey.GET_CATEGORIES]);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const handleSubCategorySubmit = (formData: any) => {
    if (parentCategory) {
      const newCategoryData: CreateCategoryPayload = {
        name: formData.name,
        description: formData.description,
        parentId: parentCategory.id,
        categoryTypeId: parentCategory.subGroup ? parentCategory.subGroup.id : parentCategory.parentGroup.id,
      };
      postNewCategory.mutate({
        payload: newCategoryData,
        type: 'newSubCategory',
      });
    }
  };

  return (
    <HookForm onSubmit={handleSubCategorySubmit}>
      <h2 className='m-0 pb-5'>Add New Sub Category</h2>
      <section className='space-y-5'>
        <HookInput type='text' label='Sub Category Name' name='name' id='subCategoryName' placeholder='e.g. Furniture' required />
        <HookTextarea name='description' label='Description' />
      </section>
      <div className='flex gap-2 justify-end mt-5'>
        <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />

        <Button label='Add' btnStyle='btn-primary' type='submit' />
      </div>
    </HookForm>
  );
};

export const NewSubCategoryForm = (props: NewCategoryFormProps) => {
  return (
    <Modal>
      <HookFormProvider>
        <NewSubCategoryBaseForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};
